
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Legend, Line, LineChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { BarChart3, Users, ArrowRightCircle, ArrowDownCircle, Settings, Ban } from "lucide-react";

interface SubscriberAnalysisProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

// Sample data for subscriber funnel
const funnelData = [
  { name: "Readers", value: 10000, icon: Users },
  { name: "Account Created", value: 2500, icon: Settings },
  { name: "Free Trial", value: 1200, icon: ArrowRightCircle },
  { name: "Paid Subscriber", value: 450, icon: ArrowRightCircle },
  { name: "Churned", value: 85, icon: Ban },
];

// Sample data for subscriber growth
const subscriberGrowthData = [
  { month: "Jan", subscribers: 120 },
  { month: "Feb", subscribers: 145 },
  { month: "Mar", subscribers: 180 },
  { month: "Apr", subscribers: 210 },
  { month: "May", subscribers: 250 },
  { month: "Jun", subscribers: 310 },
  { month: "Jul", subscribers: 355 },
  { month: "Aug", subscribers: 400 },
  { month: "Sep", subscribers: 450 },
];

// Sample data for content preferences
const contentPreferencesData = [
  { subject: "Politics", subscribers: 80, potential: 100 },
  { subject: "Technology", subscribers: 95, potential: 100 },
  { subject: "Culture", subscribers: 65, potential: 100 },
  { subject: "Science", subscribers: 78, potential: 100 },
  { subject: "Business", subscribers: 85, potential: 100 },
  { subject: "Health", subscribers: 70, potential: 100 },
];

const SubscriberAnalysis: React.FC<SubscriberAnalysisProps> = ({ timeframe }) => {
  return (
    <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
      <Card className="md:col-span-2 dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Subscriber Acquisition Funnel</CardTitle>
          <CardDescription>Conversion rates at each stage of the subscriber journey</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            {funnelData.map((item, index) => {
              const Icon = item.icon;
              const nextValue = funnelData[index + 1]?.value ?? 0;
              const conversionRate = index < funnelData.length - 1 
                ? Math.round((nextValue / item.value) * 100) 
                : Math.round((item.value / funnelData[index - 1].value) * 100);
              
              return (
                <div key={index} className="flex flex-col items-center">
                  <div className="flex flex-col items-center w-40">
                    <div className={`w-32 h-32 rounded-full flex items-center justify-center ${index === 0 ? 'bg-directpress-orange' : 'bg-directpress-blue'} bg-opacity-20`}>
                      <div className={`w-24 h-24 rounded-full flex items-center justify-center ${index === 0 ? 'bg-directpress-orange' : 'bg-directpress-blue'} bg-opacity-40`}>
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${index === 0 ? 'bg-directpress-orange' : 'bg-directpress-blue'} text-white`}>
                          <Icon className="h-8 w-8" />
                        </div>
                      </div>
                    </div>
                    <div className="text-center mt-2">
                      <div className="font-bold">{item.name}</div>
                      <div className="text-2xl font-bold">{item.value.toLocaleString()}</div>
                      {index < funnelData.length - 1 ? (
                        <div className="text-sm text-muted-foreground">
                          {conversionRate}% conversion →
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          {conversionRate}% churn rate
                        </div>
                      )}
                    </div>
                  </div>
                  {index < funnelData.length - 1 && (
                    <div className="hidden md:block h-8 w-4">
                      <ArrowRightCircle className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  {index < funnelData.length - 1 && (
                    <div className="block md:hidden h-8 w-4">
                      <ArrowDownCircle className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Subscriber Growth</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              subscribers: { 
                theme: { light: '#2563EB', dark: '#2563EB' } 
              }
            }}
            className="h-80"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={subscriberGrowthData}>
                <XAxis dataKey="month" />
                <YAxis />
                <ChartTooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <ChartTooltipContent payload={payload} />
                      );
                    }
                    return null;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="subscribers" 
                  stroke="var(--color-subscribers, #2563EB)" 
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Subscriber Content Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              subscribers: { 
                theme: { light: '#F97316', dark: '#F97316' } 
              },
              potential: { 
                theme: { light: '#9CA3AF', dark: '#6B7280' } 
              }
            }}
            className="h-80"
          >
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart outerRadius={90} data={contentPreferencesData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis domain={[0, 100]} />
                <Radar
                  name="Subscribers"
                  dataKey="subscribers"
                  stroke="var(--color-subscribers, #F97316)"
                  fill="var(--color-subscribers, #F97316)"
                  fillOpacity={0.6}
                />
                <Radar
                  name="Potential"
                  dataKey="potential"
                  stroke="var(--color-potential, #9CA3AF)"
                  fill="var(--color-potential, #9CA3AF)"
                  fillOpacity={0.2}
                />
                <Legend />
                <ChartTooltip />
              </RadarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriberAnalysis;
